/**
 * useImageActions Hook
 * 
 * Stellt Aktionen für Bilder bereit (Download, Delete, Publish, Variation).
 * Nutzt TanStack Query Mutations für optimistisches UI-Update.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  deleteImage,
  publishImage,
  downloadImage,
  regenerateImage,
  generateVariation,
} from '@/lib/api/images';

export function useImageActions() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // ===========================================
  // DELETE
  // ===========================================
  
  const deleteMutation = useMutation({
    mutationFn: deleteImage,
    onSuccess: () => {
      toast.success(t('toast.success.imageDeletedSuccess'));
      // Invalidiere beide Caches (alte + neue Galerie)
      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.deleting'));
    },
  });

  // ===========================================
  // PUBLISH
  // ===========================================
  
  const publishMutation = useMutation({
    mutationFn: publishImage,
    onSuccess: (data) => {
      const rewardMsg = data.reward ? ` +${data.reward.toFixed(2)}€ Gutschrift!` : '';
      toast.success(`${t('toast.success.imageApproved')}${rewardMsg}`);
      // Invalidiere beide Caches (alte + neue Galerie)
      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.publishing'));
    },
  });

  // ===========================================
  // DOWNLOAD
  // ===========================================
  
  const downloadMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const blob = await downloadImage(imageId);
      // Download triggern
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `goki_image_${imageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast.success(t('toast.success.downloadStarted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.downloading'));
    },
  });

  // ===========================================
  // REGENERATE
  // ===========================================
  
  const regenerateMutation = useMutation({
    mutationFn: regenerateImage,
    onSuccess: (data) => {
      toast.success(data.message || t('toast.success.imageRegenerating'));
      // Invalidiere beide Caches (alte + neue Galerie)
      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'config'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.regeneration'));
    },
  });

  // ===========================================
  // VARIATION
  // ===========================================
  
  const variationMutation = useMutation({
    mutationFn: generateVariation,
    onSuccess: (data) => {
      toast.success(data.message || t('toast.success.variationGenerating'));
      // Invalidiere beide Caches (alte + neue Galerie)
      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'config'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.variationFailed'));
    },
  });

  return {
    // Mutations
    deleteImage: deleteMutation.mutate,
    publishImage: publishMutation.mutate,
    downloadImage: downloadMutation.mutate,
    regenerateImage: regenerateMutation.mutate,
    generateVariation: variationMutation.mutate,
    
    // Loading States
    isDeleting: deleteMutation.isPending,
    isPublishing: publishMutation.isPending,
    isDownloading: downloadMutation.isPending,
    isRegenerating: regenerateMutation.isPending,
    isGeneratingVariation: variationMutation.isPending,
    
    // Any action loading?
    isAnyActionPending:
      deleteMutation.isPending ||
      publishMutation.isPending ||
      downloadMutation.isPending ||
      regenerateMutation.isPending ||
      variationMutation.isPending,
  };
}

export default useImageActions;


